import { useEffect, useRef } from 'react';

const MAX_COLORS = 8;
const hexToRGB = (hex) => {
  const c = hex.replace('#', '').padEnd(6, '0');
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  return [r, g, b];
};

const prepStops = (stops) => {
  const base = (stops && stops.length ? stops : ['#FF9FFC', '#5227FF']).slice(0, MAX_COLORS);
  if (base.length === 1) base.push(base[0]);
  while (base.length < MAX_COLORS) base.push(base[base.length - 1]);
  const arr = [];
  for (let i = 0; i < MAX_COLORS; i++) arr.push(hexToRGB(base[i]));
  const count = Math.max(2, Math.min(MAX_COLORS, stops?.length ?? 2));
  return { arr, count };
};

const GradientBlinds = ({
  className = '',
  dpr,
  paused = false,
  gradientColors,
  angle = 0,
  noise = 0.3,
  blindCount = 16,
  blindMinWidth = 60,
  minBlindCount = 8,
  mouseDampening = 0.15,
  mirrorGradient = false,
  spotlightRadius = 0.5,
  spotlightSoftness = 1,
  spotlightOpacity = 1,
  distortAmount = 0,
  shineDirection = 'left',
  mixBlendMode = 'normal',
  interactionOnHover = true,
  initialSpotlightX = 0.9,
  initialSpotlightY = 0.5,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const mouseTargetRef = useRef([0, 0]);
  const currentMouseRef = useRef([0, 0]);
  const lastTimeRef = useRef(0);
  const isHoverRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const canvas = document.createElement('canvas');
    canvasRef.current = canvas;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    container.appendChild(canvas);

    const gl = canvas.getContext('webgl2', { alpha: true, antialias: true });
    if (!gl) return;

    const vertexSrc = `#version 300 es
      in vec2 position;
      out vec2 vUv;
      void main() {
        vUv = position * 0.5 + 0.5;
        gl_Position = vec4(position, 0.0, 1.0);
      }`;

    const fragmentSrc = `#version 300 es
      precision mediump float;
      uniform vec3 iResolution;
      uniform vec2 iMouse;
      uniform float iTime;
      uniform float uAngle, uNoise, uBlindCount, uSpotlightRadius, uSpotlightSoftness, uSpotlightOpacity, uMirror, uDistort, uShineFlip;
      uniform vec3 uColors[8];
      uniform int uColorCount;
      in vec2 vUv;
      out vec4 fragColor;

      float rand(vec2 co){ return fract(sin(dot(co, vec2(12.9898,78.233))) * 43758.5453); }
      vec2 rotate2D(vec2 p, float a){ float c = cos(a), s = sin(a); return mat2(c, -s, s, c) * p; }

      vec3 getGradientColor(float t){
        float tt = clamp(t, 0.0, 1.0);
        float scaled = tt * float(uColorCount - 1);
        int seg = int(floor(scaled));
        float f = fract(scaled);
        if (seg == 0) return mix(uColors[0], uColors[1], f);
        if (seg == 1) return mix(uColors[1], uColors[2], f);
        if (seg == 2) return mix(uColors[2], uColors[3], f);
        if (seg == 3) return mix(uColors[3], uColors[4], f);
        if (seg == 4) return mix(uColors[4], uColors[5], f);
        if (seg == 5) return mix(uColors[5], uColors[6], f);
        if (seg == 6) return mix(uColors[6], uColors[7], f);
        return uColors[uColorCount - 1];
      }

      void main() {
        vec2 uv0 = vUv;
        float aspect = iResolution.x / iResolution.y;
        vec2 p = uv0 * 2.0 - 1.0;
        p.x *= aspect;
        vec2 pr = rotate2D(p, uAngle);
        pr.x /= aspect;
        vec2 uv = pr * 0.5 + 0.5;

        vec2 uvMod = uv;
        if (uDistort > 0.0) {
          uvMod.x += sin(uvMod.y * 6.0) * (0.01 * uDistort);
          uvMod.y += cos(uvMod.x * 6.0) * (0.01 * uDistort);
        }
        float t = uMirror > 0.5 ? 1.0 - abs(1.0 - 2.0 * fract(uvMod.x)) : uvMod.x;
        vec3 base = getGradientColor(t);

        float d = length(uv0 - (iMouse.xy / iResolution.xy));
        float spot = (1.0 - 2.0 * pow(d / max(uSpotlightRadius, 0.001), uSpotlightSoftness)) * uSpotlightOpacity;
        float stripe = fract(uvMod.x * max(uBlindCount, 1.0));
        if (uShineFlip > 0.5) stripe = 1.0 - stripe;

        vec3 col = vec3(spot) + base - vec3(stripe);
        col += (rand(gl_FragCoord.xy + iTime) - 0.5) * uNoise;
        fragColor = vec4(col, 1.0);
      }`;

    const createShader = (gl, type, source) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, source);
      gl.compileShader(s);
      return s;
    };

    const program = gl.createProgram();
    gl.attachShader(program, createShader(gl, gl.VERTEX_SHADER, vertexSrc));
    gl.attachShader(program, createShader(gl, gl.FRAGMENT_SHADER, fragmentSrc));
    gl.linkProgram(program);
    gl.useProgram(program);

    const vertices = new Float32Array([-1, -1, 3, -1, -1, 3]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const posLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const locs = {
      res: gl.getUniformLocation(program, 'iResolution'),
      mouse: gl.getUniformLocation(program, 'iMouse'),
      time: gl.getUniformLocation(program, 'iTime'),
      angle: gl.getUniformLocation(program, 'uAngle'),
      noise: gl.getUniformLocation(program, 'uNoise'),
      blinds: gl.getUniformLocation(program, 'uBlindCount'),
      radius: gl.getUniformLocation(program, 'uSpotlightRadius'),
      soft: gl.getUniformLocation(program, 'uSpotlightSoftness'),
      opac: gl.getUniformLocation(program, 'uSpotlightOpacity'),
      mirror: gl.getUniformLocation(program, 'uMirror'),
      distort: gl.getUniformLocation(program, 'uDistort'),
      flip: gl.getUniformLocation(program, 'uShineFlip'),
      count: gl.getUniformLocation(program, 'uColorCount'),
      colors: gl.getUniformLocation(program, 'uColors'),
    };

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const pixelRatio = dpr ?? window.devicePixelRatio;
      canvas.width = rect.width * pixelRatio;
      canvas.height = rect.height * pixelRatio;
      gl.viewport(0, 0, canvas.width, canvas.height);
      const computed = blindMinWidth > 0 ? Math.floor(rect.width / blindMinWidth) : blindCount;
      const effectiveBlinds = Math.max(minBlindCount, Math.min(blindCount, computed));
      gl.uniform1f(locs.blinds, Math.max(1, effectiveBlinds));
      gl.uniform3f(locs.res, canvas.width, canvas.height, 1);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();

    const onPointerMove = (e) => {
      const rect = container.getBoundingClientRect();
      const px = dpr ?? window.devicePixelRatio;
      const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      isHoverRef.current = inside;
      if (interactionOnHover && !inside) {
        mouseTargetRef.current = [canvas.width * initialSpotlightX, canvas.height * initialSpotlightY];
        return;
      }
      mouseTargetRef.current = [(e.clientX - rect.left) * px, (rect.height - (e.clientY - rect.top)) * px];
    };
    window.addEventListener('pointermove', onPointerMove);

    const { arr: colorArr, count: colorCount } = prepStops(gradientColors);

    currentMouseRef.current = [canvas.width * initialSpotlightX, canvas.height * initialSpotlightY];
    mouseTargetRef.current = [canvas.width * initialSpotlightX, canvas.height * initialSpotlightY];

    const render = (t) => {
      if (paused) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }
      const dt = (t - lastTimeRef.current) / 1000;
      lastTimeRef.current = t;
      const factor = mouseDampening <= 0 ? 1 : 1 - Math.exp(-dt / Math.max(1e-4, mouseDampening));
      currentMouseRef.current[0] += (mouseTargetRef.current[0] - currentMouseRef.current[0]) * factor;
      currentMouseRef.current[1] += (mouseTargetRef.current[1] - currentMouseRef.current[1]) * factor;

      gl.uniform1f(locs.time, t * 0.001);
      gl.uniform2fv(locs.mouse, currentMouseRef.current);
      gl.uniform1f(locs.angle, (angle * Math.PI) / 180);
      gl.uniform1f(locs.noise, noise);
      gl.uniform1f(locs.radius, spotlightRadius);
      gl.uniform1f(locs.soft, spotlightSoftness);
      gl.uniform1f(locs.opac, spotlightOpacity);
      gl.uniform1f(locs.mirror, mirrorGradient ? 1 : 0);
      gl.uniform1f(locs.distort, distortAmount);
      gl.uniform1f(locs.flip, shineDirection === 'right' ? 1 : 0);
      gl.uniform1i(locs.count, colorCount);
      gl.uniform3fv(locs.colors, new Float32Array(colorArr.flat()));

      gl.drawArrays(gl.TRIANGLES, 0, 3);
      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      if (canvas.parentElement) container.removeChild(canvas);
      gl.deleteProgram(program);
      gl.deleteBuffer(buffer);
    };
  }, [
    dpr,
    paused,
    gradientColors,
    angle,
    noise,
    blindCount,
    blindMinWidth,
    mouseDampening,
    mirrorGradient,
    spotlightRadius,
    spotlightSoftness,
    spotlightOpacity,
    distortAmount,
    shineDirection,
    interactionOnHover,
    initialSpotlightX,
    initialSpotlightY,
  ]);

  return (
    <div ref={containerRef} className={`absolute inset-0 ${className}`} style={{ mixBlendMode }} />
  );
};

export default GradientBlinds;
